// --- React
import React, { useCallback } from 'react';
// --- Hooks
import { useDialogsActions } from '../hooks';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
} from '@dtci/spellbook/Table';
import moment from 'moment';

/** * CentFileForm Component
 *
 * This component is responsible for rendering the central file numbers list.
 *
 * @component
 * @param {Object} dtaa - The message object containing central file number information.
 * @returns {JSX.Element} The rendered CentFileForm component.
 */
export const CentFileForm = ({ data }) => {
    const tableHeaders = ['File Number', 'Modified By', 'Modified Date'];

    return (
        <div className="flexBox">
            {/* Render the table with headers and data */}
            <Table bordered="horizontal">
                <TableHead>
                    <TableRow>
                        {/* Render the headers of the table */}
                        {tableHeaders.map((header, idx) => (
                            <TableHeaderCell key={header}>{header}</TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHead>
                {/* Render the body of the table */}
                <TableBody>
                    {data?.centralFileNumbers ? (
                        data?.centralFileNumbers?.map((data, index) => (
                            <TableRow key={index}>
                                <TableCell>{data?.fileNumber}</TableCell>
                                <TableCell>{data?.modifiedBy.fullName}</TableCell>
                                <TableCell>
                                    {moment(data?.modifiedAt).format('MM/DD/YYYY')}
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow key={window.crypto.randomUUID()}>
                            <TableCell>--</TableCell>
                            <TableCell>--</TableCell>
                            <TableCell>--</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

/**
 * useCentFileDialog Hook
 *
 * This hook provides functionality to open a central file numbers dialog.
 * It uses the useDialogsActions hook to manage the dialog state.
 *
 * @returns {Object} An object containing the openCentFileDialog function.
 */
export const useCentFileDialog = () => {
    const { openDialog } = useDialogsActions();

    // Function to open the delete confirmation dialog
    const openCentFileDialog = useCallback(
        (data) => {
            openDialog({
                title: 'Central File Numbers',
                content: <CentFileForm data={data} />,
                closeAction: true,
            });
        },
        [openDialog],
    );

    return { openCentFileDialog };
};
